import {
  BackButton,
  useOrganizationUsers,
} from '@airship-platform/airship-patterns';
import { FlexBox, PageWithActions } from '@airship-platform/jam-web/components';
import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import { useAppointmentHistory } from '../../../../data-access/appointmentHistory';
import ROUTE_PATHS from '../../../../routes/paths';
import { AppointmentHistoryFormSchema } from './AppointmentSearchForm';

const statusOptions = [
  { label: 'Scheduled', value: 'SCHEDULED' },
  { label: 'Dispatched', value: 'DISPATCHED' },
  { label: 'Working', value: 'IN-PROGRESS' },
  { label: 'Done', value: 'COMPLETED' },
  { label: 'On Hold', value: 'ON-HOLD' },
  { label: 'Cancelled', value: 'CANCELLED' },
];
const proposalStatusOptions = [
  { label: 'Proposal created', value: 'PROPOSAL_CREATED' },
  { label: 'Proposal not shared', value: 'PROPOSAL_NOT_SHARED' },
  { label: 'Proposal shared', value: 'PROPOSAL_SHARED' },
  { label: 'Proposal accepted', value: 'PROPOSAL_ACCEPTED' },
];

const FORM_ID = 'appointment-search-filter-form';

const AppointmentSearchFilterFormSchema = AppointmentHistoryFormSchema.pick({
  status: true,
  proposalStatus: true,
  includeNonAirshipJobs: true,
  assignedUserIds: true,
});

export type AppointmentSearchFilterFormInputs = z.infer<
  typeof AppointmentSearchFilterFormSchema
>;

const AppointmentSearchFilter: React.FC = () => {
  const navigate = useNavigate();
  const { currentAppointmentFilter, updateHistoryFilter } =
    useAppointmentHistory();
  const { users } = useOrganizationUsers();

  const { control, formState, handleSubmit } =
    useForm<AppointmentSearchFilterFormInputs>({
      defaultValues: {
        status: currentAppointmentFilter.status,
        proposalStatus: currentAppointmentFilter.proposalStatus,
        assignedUserIds: currentAppointmentFilter.assignedUserIds,
        includeNonAirshipJobs: currentAppointmentFilter.includeNonAirshipJobs,
      },
      resolver: zodResolver(AppointmentSearchFilterFormSchema),
    });

  const onSubmitForm = (data: AppointmentSearchFilterFormInputs) => {
    updateHistoryFilter(data);
    navigate(ROUTE_PATHS.SCHEDULE_SEARCH);
  };

  const isSelected = <T,>(value: T[], optionValue: T) =>
    value.includes(optionValue);

  const getNextToggleValue = <T,>(value: T[], optionValue: T) =>
    value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue];

  const isOwnerUser = !!users.length;

  const renderHeader = (title: string, description: string) => (
    <FlexBox flexDirection={'column'} alignItems={'flex-start'}>
      <Typography variant={'h4'}>{title}</Typography>
      <Typography variant={'body2'} color={'gray'} sx={{ fontStyle: 'italic' }}>
        {description}
      </Typography>
    </FlexBox>
  );

  return (
    <PageWithActions
      actionsComponent={
        <FlexBox
          alignItems={'center'}
          justifyContent={'space-between'}
          width={'100%'}
        >
          <BackButton color={'softGold'} defaultPath={'../search'}>
            Back
          </BackButton>

          <Button
            type="submit"
            form={FORM_ID}
            color={formState.isValid ? 'primary' : 'softGold'}
          >
            Save
          </Button>
        </FlexBox>
      }
      sx={{ alignItems: 'center' }}
    >
      <Box
        id={FORM_ID}
        component={'form'}
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit(onSubmitForm)}
        sx={{
          padding: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          width: '100%',
        }}
      >
        <Typography variant={'h2'}>Filters</Typography>

        {isOwnerUser && (
          <Controller
            name={'includeNonAirshipJobs'}
            control={control}
            render={({ field }) => (
              <FlexBox
                flexDirection={'column'}
                gap={2}
                alignItems={'flex-start'}
              >
                {renderHeader(
                  'Include Non-Airship jobs',
                  'Toggle to include jobs that were not originally assigned in Airship.'
                )}

                <FormControlLabel
                  control={
                    <Switch {...field} color={'info'} checked={field.value} />
                  }
                  label={`Non-Airship jobs are ${
                    field.value ? 'included' : 'hidden'
                  }`}
                />

                <Divider flexItem />
              </FlexBox>
            )}
          />
        )}

        <Controller
          name={'status'}
          control={control}
          render={({ field }) => (
            <FlexBox flexDirection={'column'} gap={2}>
              {renderHeader('Job Status', 'Where the job is in its lifecycle.')}
              <FlexBox gap={2} flexWrap={'wrap'}>
                {statusOptions.map((option) => {
                  const selected = isSelected(field.value, option.value);
                  return (
                    <Button
                      key={option.value}
                      color={selected ? 'blue' : 'gray'}
                      variant={selected ? 'contained' : 'outlined'}
                      onClick={() =>
                        field.onChange(
                          getNextToggleValue(field.value, option.value)
                        )
                      }
                    >
                      {option.label}
                    </Button>
                  );
                })}
              </FlexBox>
              <Divider />
            </FlexBox>
          )}
        />

        <Controller
          name={'proposalStatus'}
          control={control}
          render={({ field }) => (
            <FlexBox flexDirection={'column'} gap={2}>
              {renderHeader(
                'Proposal Status',
                'Where the proposal is in the sales process.'
              )}
              <FlexBox gap={2} flexWrap={'wrap'}>
                {proposalStatusOptions.map((option) => {
                  const selected = isSelected(field.value, option.value);
                  return (
                    <Button
                      key={option.value}
                      color={selected ? 'blue' : 'gray'}
                      variant={selected ? 'contained' : 'outlined'}
                      onClick={() =>
                        field.onChange(
                          getNextToggleValue(field.value, option.value)
                        )
                      }
                    >
                      {option.label}
                    </Button>
                  );
                })}
              </FlexBox>
              <Divider />
            </FlexBox>
          )}
        />

        {isOwnerUser && (
          <Controller
            name={'assignedUserIds'}
            control={control}
            render={({ field }) => (
              <FlexBox flexDirection={'column'} gap={2}>
                {renderHeader(
                  'Employees',
                  'Employees who worked on or created proposals for this job.'
                )}
                <FlexBox gap={2} flexWrap={'wrap'}>
                  {users
                    .map((user) => ({
                      label: `${user.firstName} ${user.lastName}`,
                      value: user.userId,
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label))
                    .map((user) => {
                      const selected = isSelected(field.value, user.value);
                      return (
                        <Button
                          key={user.value}
                          color={selected ? 'blue' : 'gray'}
                          variant={selected ? 'contained' : 'outlined'}
                          onClick={() =>
                            field.onChange(
                              getNextToggleValue(field.value, user.value)
                            )
                          }
                        >
                          {user.label}
                        </Button>
                      );
                    })}
                </FlexBox>
              </FlexBox>
            )}
          />
        )}
      </Box>
    </PageWithActions>
  );
};

export default AppointmentSearchFilter;
